import React, { useState, useEffect } from "react";
import { Button } from "../../../elements/Button";
import { ReactComponent as BackArrow } from "../../../assets/icons/left_arrow.svg";
import commoncss from "../../../css/common.module.css";
import buttoncss from "../../../css/button.module.css";


import { getDeletedItemsApiHandler, restoreDeletedItemsApiHandler, permanentDeleteItemsApiHandler } from "../../../../WorkSpace/Components/ApiServices/apiHandler";
import QuillReactEditor from "../../../components/QuillReactEditor";
import TemplateTableGenerator from "../TemplateTableGenerator";
import { ReactComponent as DotIcon } from "../../../assets/icons/dot.svg";
import { ReactComponent as EmptyIcon } from "../../../assets/icons/Empty.svg";
import swal from "sweetalert";
import SuccessPopup from "../../../elements/SuccessPopup";
import DeletePopup from "../../../elements/DeletePopup";
import "../../../css/breadcrumb.css";

const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: block._id || genId(),
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block?.textRaw ?? " ",
        textPlain: block?.textPlain ?? " ",
        text: [
          {
            id: genId(),
            content: block?.textRaw ?? " ",
          },
        ],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: block._id || genId(),
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow)
          ? block.tableRow.map((r) => ({ ...r }))
          : [],
        tableColumn: Array.isArray(block.tableColumn)
          ? block.tableColumn.map((c) => ({ ...c }))
          : [],
        tableType: block?.tableType ?? "table",
        table: block?.table ?? null,
        hasCellData: block?.tableType === "content",
      });
    }
  });
  return items;
};

const LibraryDeletedItems = ({ showBin, setShowBin, tempId, templateData, tabName }) => {
  const [activeBinSection, setActiveBinSection] = useState(
    showBin === "disclosures" ? "Disclosures" : "General Notes"
  );
  const [activeBinTab, setActiveBinTab] = useState(null);
  const [selectedBinItems, setSelectedBinItems] = useState([]);
  const [deletedItems, setDeletedItems] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (tempId) {
      getDeletedItemsApiHandler({ groupingTemplateID: tempId })
        .then((res) => {
          if (res?.data?.success) {
            setDeletedItems(res.data.deletedItems || []);
          }
        })
        .catch((err) => console.log("Error fetching deleted items", err));
    }
  }, [tempId]);

  const binSections = ["General Notes", "Disclosures"];
  
  const sectionItems = deletedItems.filter(item => {
    if (activeBinSection === "General Notes") {
      return item.itemType === "generalNote" || item.itemType === "generalSubnote" || item.itemType === "subnote" || !!item.generalNoteID;
    } else {
      return item.itemType === "disclosure" || item.itemType === "disclosureSubnote" || item.itemType === "subnote" || !!item.disclosureID;
    }
  });
  
  const binTabsMap = new Map();
  sectionItems.forEach(item => {
    const isSubnote = item.itemType === "generalSubnote" || item.itemType === "disclosureSubnote" || !!item.generalNoteID || !!item.disclosureID || !!item.originalParentId || !!item.parentID || !!item.noteID;
    const parentId = isSubnote ? (item.generalNoteID || item.disclosureID || item.parentID || item.originalParentId || item.noteID) : item._id;
    
    if (!parentId) return;

    if (!binTabsMap.has(parentId)) {
      binTabsMap.set(parentId, {
        _id: parentId,
        name: isSubnote ? (item.parentName || item.generalNoteName || item.disclosureName || item.parentNoteName || "Note") : (item.name || item.groupingTemplateName || "Note"),
        mainNote: null,
        subNotes: []
      });
    }
    
    const parentObj = binTabsMap.get(parentId);
    if (isSubnote) {
      parentObj.subNotes.push(item);
    } else {
      parentObj.mainNote = item;
      parentObj.name = item.name || item.groupingTemplateName || "Note"; 
    }
  });

  const binTabs = Array.from(binTabsMap.values());

  useEffect(() => {
    if (binTabs.length > 0 && (!activeBinTab || !binTabs.find(t => t._id === activeBinTab._id))) {
      setActiveBinTab(binTabs[0]);
    } else if (binTabs.length === 0 && activeBinTab !== null) {
      setActiveBinTab(null);
    }
  }, [activeBinSection, deletedItems, binTabs.length]);

  const cleanTableData = (di, section) => {
    if (section !== "Disclosures") return di;
    const hideKeys = ["issubrow", "isnewrow", "isnew", "name"];
    
    const filterObj = (obj) => {
      if (!obj) return obj;
      const newObj = { ...obj };
      Object.keys(newObj).forEach(key => {
        if (hideKeys.includes(key.toLowerCase())) {
          delete newObj[key];
        }
      });
      return newObj;
    };

    return {
      tableTitle: di.tableTitle,
      tableType: di.tableType,
      hasCellData: di.hasCellData,
      tableColumn: (di.tableColumn || []).filter(c => !hideKeys.includes((c.name || "").toLowerCase())),
      tableRow: (di.tableRow || []).map(filterObj),
      table: Array.isArray(di.table) ? di.table.map(filterObj) : di.table,
    };
  };

  const renderDataBlocks = (doc) => {
    if (!doc) return null;
    let items = [];
    if (Array.isArray(doc.data)) {
      items = normalizeFromInitial({ data: doc.data });
    } else if (doc.data && Array.isArray(doc.data.data)) {
      items = normalizeFromInitial(doc.data);
    } else if (doc.data) {
      items = normalizeFromInitial({ data: [doc.data] });
    }
    return items.map((di) => (
      <div
        key={di.id || di._id}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "15px",
          padding: "15px 0",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, overflow: "hidden" }}>
          {di.type === "table" && (
            <div
              style={{
                background: "#f8f9f9",
                display: "flex",
                alignItems: "center",
                padding: "8px 15px",
                borderRadius: "10px 10px 0 0",
                border: "1px solid #ccd5d3",
                borderBottom: "none",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontWeight: 600, color: "#333", fontSize: "14px" }}>
                {di.tableTitle || "Table"}
              </div>
            </div>
          )}

          {di.type === "text" ? (
            <QuillReactEditor
              value={di.text?.[0]?.content ?? ""}
              onChange={() => {}}
              disabled={true}
              rightControl={null}
            />
          ) : di.type === "table" ? (
            <div
              style={{
                border: "none",
                borderRadius: "0 0 10px 10px",
                background: "#fff",
                padding: 0,
                overflow: "hidden",
              }}
            >
              <TemplateTableGenerator
                forceOpen={false}
                hideRowBoldIcon={true}
                value={cleanTableData(di, activeBinSection)}
                onChange={() => {}}
                onAfterOpen={() => {}}
                disabled={true}
                hideHeader={true}
              />
            </div>
          ) : (
            <div>{di.name || "Unknown Note Type"}</div>
          )}
        </div>
      </div>
    ));
  };


  const handleRestoreSelected = () => {
    if (selectedBinItems.length === 0) return;
    
    restoreDeletedItemsApiHandler({ groupingTemplateID: tempId }, { deletedItemID: selectedBinItems })
      .then((res) => {
        if (res?.data?.success) {
          setSuccessMessage("Restored successfully");
          setShowSuccessPopup(true);
          setSelectedBinItems([]);
          getDeletedItemsApiHandler({ groupingTemplateID: tempId }).then((res) => {
            if (res?.data?.success) {
              setDeletedItems(res.data.deletedItems || []);
            }
          });
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.error || "Unable to restore selected items";
        swal("Error", msg, "error");
      });
  };

  const handleDeletePermanently = () => {
    if (selectedBinItems.length === 0) return;
    setDeleteConfirm({
      title: "Confirm deletion",
      message: "This item will be deleted permanently",
      onConfirm: () => {
        setDeleteConfirm(null);
        permanentDeleteItemsApiHandler({ groupingTemplateID: tempId }, { deletedItemID: selectedBinItems })
          .then((res) => {
            if (res?.data?.success) {
              setSuccessMessage("Delete successful");
              setShowSuccessPopup(true);
              setSelectedBinItems([]);
              getDeletedItemsApiHandler({ groupingTemplateID: tempId }).then((res) => {
                if (res?.data?.success) {
                  setDeletedItems(res.data.deletedItems || []);
                }
              });
            }
          })
          .catch((error) => {
            const msg = error?.response?.data?.error || "Unable to delete the item";
            swal("Error", msg, "error");
          });
      }
    });
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", paddingBottom: "10px" }}>
      {/* Header */}
      <div
        className={`${commoncss.pageContentHeader} ${commoncss.dFlexJustifysb} fscp-header`}
        style={{ paddingLeft: "20px", paddingRight: "0px", paddingBottom: "10px", paddingTop: "15px" }}
      >
        <div style={{ display: "flex", alignItems: "center", marginRight: "auto" }}>
          <header className="breadcrumb-header bgClr1" style={{ padding: 0 }}>
            <span className="breadcrumb-title">
              <span
                onClick={() => setShowBin(null)}
                style={{ 
                  color: "inherit", 
                  textDecoration: "none", 
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "5px 4px 5px 0px",
                  marginLeft: "0px"
                }}
              >
                <BackArrow className="clickableMiddle" style={{ marginLeft: "0px", marginRight: "10px", cursor: "pointer", transform: "translateY(2px)" }} />
              </span>
              <label className={`${commoncss.nav_cnt_color} clickable`}>Template Library</label>
              &ensp;&nbsp;
              <DotIcon className="vaMiddle" /> &ensp;&nbsp;
              <label className={`${commoncss.nav_cnt_color}`}>{templateData?.templateName || templateData?.name || templateData?.groupingTemplateName || "BaseTemplate"}</label>
              &ensp;&nbsp;
              {/* <DotIcon className="vaMiddle" /> &ensp;&nbsp;
              <label className="nav_cnt_color">{tabName || "Notes"}</label>
              &ensp;&nbsp; */}
              <DotIcon className="vaMiddle" /> &ensp;&nbsp;
              <label className="headerLable semibold-label">Deleted Items</label>
            </span>
          </header>
        </div>
        <div
          className={commoncss.dFlexAlignCenter}
          style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" }}
        >
          {selectedBinItems.length > 0 && (
            <span
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#205a46",
                alignContent: "center",
              }}
            >
              ({selectedBinItems.length}) SELECTED
            </span>
          )}
          <Button
            btnColor={buttoncss.secondary}
            style={{
              color: "#E30000",
              borderColor: "#CCD5D3",
              backgroundColor: "transparent",
            }}
            label="Delete Permanently "
            onClick={handleDeletePermanently}
          />
          <Button
            btnColor={buttoncss.primary}
            label="Restore Selected Items"
            onClick={handleRestoreSelected}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "0px 20px", flex: 1, overflowY: "auto", marginTop: "20px" }}>
        <div
          style={{
            background: "#fff",
            height: "calc(100vh - 120px)",
            display: "flex",
            borderRadius: "14px",
            border: "1px solid #ccd5d3",
            overflow: "hidden",
          }}
        >
          {/* Left Menu Container */}
          <div
            style={{
              width: "250px",
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #ccd5d3",
            }}
          >
            {binSections.map((section) => (
              <div
                key={section}
                onClick={() => {
                  setActiveBinSection(section);
                }}
                style={{
                  padding: "15px 20px",
                  cursor: "pointer",
                  background:
                    activeBinSection === section ? "#E4F2E6" : "transparent",
                  borderRight:
                    activeBinSection === section
                      ? "1px solid #205a46"
                      : "transparent",
                  color: activeBinSection === section ? "#205a46" : "#333",
                  fontSize: "14px",
                  fontWeight: activeBinSection === section ? 600 : 400,
                }}
              >
                {section}
              </div>
            ))}
          </div>

          {/* Right Content Container */}
          <div
            style={{
              flex: 1,
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                border: "1px solid #ccd5d3",
                borderRadius: "8px",
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                style={{ display: "flex", borderBottom: "1px solid #ccd5d3" }}
              >
                {binTabs.map((tab) => (
                  <div
                    key={tab._id}
                    onClick={() => setActiveBinTab(tab)}
                    style={{
                      padding: "15px 30px",
                      background:
                        activeBinTab?._id === tab._id ? "#E4F2E6" : "transparent",
                      borderBottom:
                        activeBinTab?._id === tab._id
                          ? "1px solid #205a46"
                          : "transparent",
                      color: activeBinTab?._id === tab._id ? "#205a46" : "#666",
                      fontWeight: activeBinTab?._id === tab._id ? 600 : 500,
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    {tab.name || "Unnamed Tab"}
                  </div>
                ))}
              </div>
              <div
                className={commoncss.hideScroll}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "20px",
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                {activeBinTab ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        paddingBottom: "15px",
                      }}
                    >
                      {(() => {
                        const allIdsInActiveTab = [
                          ...(activeBinTab.mainNote ? [activeBinTab.mainNote._id] : []),
                          ...activeBinTab.subNotes.map(s => s._id)
                        ];
                        const isActiveTabSelected = allIdsInActiveTab.length > 0 && allIdsInActiveTab.every(id => selectedBinItems.includes(id));
                        return (
                          <input
                            type="checkbox"
                            checked={isActiveTabSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedBinItems((prev) => Array.from(new Set([...prev, ...allIdsInActiveTab])));
                              } else {
                                setSelectedBinItems((prev) => prev.filter(id => !allIdsInActiveTab.includes(id)));
                              }
                            }}
                            style={{
                              accentColor: "#205a46",
                              width: "16px",
                              height: "16px",
                            }}
                          />
                        );
                      })()}
                      <span
                        style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}
                      >
                        Select {activeBinTab?.name || "Item"}
                      </span>
                    </div>

                    {renderDataBlocks(activeBinTab.mainNote)}
                    {(() => {
                      const nativeSubnotes = activeBinTab.mainNote?.subNotes || activeBinTab.mainNote?.subnotes || activeBinTab.mainNote?.data?.subNotes || activeBinTab.mainNote?.data?.subnotes || [];
                      const groupedSubnotes = activeBinTab.subNotes || activeBinTab.subnotes || [];
                      const combinedSubnotes = [...nativeSubnotes, ...groupedSubnotes];
                      
                      return combinedSubnotes.map((sub, idx) => (
                        <div key={sub._id || idx}>
                          <div
                            style={{
                              marginTop: "10px",
                              marginBottom: "10px",
                              marginLeft: "-20px",
                              marginRight: "-20px",
                              paddingLeft: "20px",
                              paddingRight: "20px",
                              paddingTop: "10px",
                              paddingBottom: "10px",
                              borderBottom: "1px solid #ccd5d3",
                              borderTop: "1px solid #ccd5d3",
                              fontWeight: 900,
                              fontSize: "14px",
                              color: "#183930",
                            }}
                          >
                            {sub.name || `Sub-note ${idx + 1}`}
                          </div>
                          {renderDataBlocks(sub)}
                        </div>
                      ));
                    })()}
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      minHeight: "300px",
                    }}
                  >
                    <EmptyIcon />
                    <div style={{ color: "#183930", fontWeight: 600, fontSize: "20px", marginTop: "15px" }}>
                      Nothing here yet
                    </div>
                    <div style={{ color: "#4D6D64", fontWeight: 400, fontSize: "16px", marginTop: "5px" }}>
                      There are no deleted items
                    </div>
                    
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SuccessPopup
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        message={successMessage || "Restored successfully"}
      />
      <DeletePopup
        isOpen={deleteConfirm !== null}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        onConfirm={deleteConfirm?.onConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default LibraryDeletedItems;
